import React from 'react';
import type { TFunction } from 'i18next';
import { Badge } from 'react-bootstrap';

import { Grid } from '../../../common/components/Grid/Grid';
import { GridColumn } from '../../../common/components/Grid/GridDataTypes';
import { GetQuestionAndAnswerDto } from '../../../common/pokCore/autogenerated/pokApiClient';
import { TKeys, useTranslation } from '../../../common/hooks/useTranslation';
import momencik from '../../../common/momencik';
import { YesNoEnum } from '../../../common/pokCore/validation/enums';
import { usePokCore } from '../../../common/hooks/usePokCore';

interface SurveyAnswerSearchProps {
  data: GetQuestionAndAnswerDto[];
}

const renderYesNoAnswer = (
  t: TFunction<'administrationLng', 'administrationLng'>,
  tk: TKeys<'administrationLng'>,
  item: GetQuestionAndAnswerDto,
) => {
  if (!item.answer) {
    return '';
  }

  if (
    item.answer.booleanValue !== null &&
    item.answer.booleanValue !== undefined
  ) {
    const answerValue = item.answer.booleanValue
      ? t(tk.common.yes)
      : t(tk.common.no);

    return (
      <div className="d-flex align-items-center gap-2">
        <strong>{answerValue}</strong>
      </div>
    );
  }

  return '';
};

const unexpectedAnswer = (item: GetQuestionAndAnswerDto) => {
  if (!item.answer) {
    return false;
  }
  if (
    item.answer.booleanValue !== null &&
    item.answer.booleanValue !== undefined
  ) {
    const isExpectedAnswer = item.question.expectedYesNoAnswer
      ? (item.question.expectedYesNoAnswer === YesNoEnum.Yes &&
          item.answer.booleanValue) ||
        (item.question.expectedYesNoAnswer === YesNoEnum.No &&
          !item.answer.booleanValue)
      : true;
    return !isExpectedAnswer;
  }
  return false;
};

const renderBadges = (
  t: TFunction<'administrationLng', 'administrationLng'>,
  tk: TKeys<'administrationLng'>,
  item: GetQuestionAndAnswerDto,
) => {
  return (
    <div className="d-flex gap-2 flex-wrap">
      {unexpectedAnswer(item) && item.question.warningLight && (
        <Badge bg="danger" className="text-white fs-6">
          {t(tk.survey.warningLightShort)}
        </Badge>
      )}
      {unexpectedAnswer(item) && (
        <Badge bg="warning" className="text-dark fs-6">
          {t(tk.survey.badAnswer)}
        </Badge>
      )}
    </div>
  );
};

export const SurveyAnswerSearch: React.FC<SurveyAnswerSearchProps> = props => {
  const { t, tk } = useTranslation('administrationLng');
  const pok = usePokCore();
  const lang = pok.getLanguage();

  const data = props.data.map(item => {
    return {
      key: item.question.id,
      values: {
        id: item.question.id,
        order: item.question.order + 1,
        question: (
          <span
            dangerouslySetInnerHTML={{
              __html:
                lang === 'PL'
                  ? item.question.question
                  : item.question.questionEn,
            }}
          />
        ),
        badges: renderBadges(t, tk, item),
        answerYesNo: renderYesNoAnswer(t, tk, item),
        answerText: item?.answer?.textValue || '',
        answeredDate: item.answer
          ? momencik(item.answer.ts, 'YYYY-MM-DD HH:mm:ss')
          : '',
      },
      orderByNumberValues: {
        order: item.question.order,
      },
    };
  });

  const columns: GridColumn[] = [
    {
      header: t(tk.common.number),
      property: 'order',
    },
    {
      header: t(tk.common.alert),
      property: 'badges',
      noFilter: true,
      noSorting: true,
    },
    {
      header: t(tk.survey.question),
      property: 'question',
      noFilter: true,
      noSorting: true,
    },

    {
      header: t(tk.survey.answerYesNo),
      property: 'answerYesNo',
    },
    {
      header: t(tk.survey.answerText),
      property: 'answerText',
    },
    {
      header: t(tk.clientSurvey.answeredDate),
      property: 'answeredDate',
    },
  ];

  return (
    <Grid
      data={data}
      columns={columns}
      showFilter={false}
      initialOrderColumn="order"
      availablePageSizes={[10, 20, 50]}
      pageSize={50}
      emptyGridMessage={t(tk.common.noData)}
      withHistory={false}
      withFilterInColumns={false}
    />
  );
};
